import { isUndefined, map } from "lodash";
import { useInjection } from "@linkfluence/core/di";
import { useCurrentUserTimezone } from "@linkfluence/core/user/use-cases";
import { PeriodRange } from "@linkfluence/models/date";
import { Facet } from "@linkfluence/models/facet";
import { useQuery } from "@tanstack/react-query";
import { useContextualFocuses } from "@radarly/core/context/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { Clause } from "@radarly/models/clause";
import { getInnerClauseFromClause } from "@radarly/models/clause/getter";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { ImageClustersData } from "@radarly/models/insights";
import { getCoverFallbackFromClusterPost } from "@radarly/models/picture";
import { Project } from "@radarly/models/project";
import {
  InsightsRepository,
  InsightsRepositoryUID,
} from "../repositories/InsightsRepository";

const addImageFallbacks = (data: ImageClustersData, project: Project) => {
  return data && data.clusters
    ? {
        ...data,
        clusters: map(data.clusters, (cluster) => ({
          ...cluster,
          hits: map(cluster.hits, (post) => ({
            ...post,
            srcFallback: getCoverFallbackFromClusterPost(post, project),
          })),
        })),
      }
    : data;
};
export function useImageClusters(
  workspaceId: number,
  clause: Clause,
  period: PeriodRange
) {
  const timezone = useCurrentUserTimezone();
  const repository = useInjection<InsightsRepository>(InsightsRepositoryUID);
  const contextualFocuses = useContextualFocuses();
  const focusClause = getInnerClauseFromClause(clause, Facet.Focuses);
  const project = useCurrentProject();
  const refinedClause = {
    clause: isUndefined(focusClause)
      ? refineClauseWithInnerClause(clause, {
          [Facet.Focuses]: map(contextualFocuses, "id"),
        })
      : clause,
    from: period?.from,
    to: period?.to,
    tz: timezone,
    and: [],
  };
  const params = {
    ...clause,
    query: refinedClause,
  };
  const { data, isLoading: loading } = useQuery(
    [
      "/insights/getImageClusters",
      {
        projectId: project.id,
        workspaceId,
        clause: params,
      },
    ],
    () => {
      return repository.getImageClusters(project.id, params);
    }
  );
  return {
    loading,
    data: addImageFallbacks(data, project),
  };
}
